/**
 * Page header of every extranet screen (design `page-header`): optional
 * « Retour » button, breadcrumb (home icon › … › current), title, subtitle
 * and an actions slot (badges, buttons).
 *
 * Pure chrome — not a registered configurable component. The page passes its
 * own texts; the fixed strings live in {@link PAGE_HEADER_TEXT}.
 */

import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Home } from 'lucide-react';

import { Button } from '@components/base/actions';
import { Icon, Text } from '@components/base/display';
import { HStack, VStack } from '@components/base/layout';
import { Breadcrumb } from '@components/base/navigation';

import { extranetPath } from '../navigation';
import { PAGE_HEADER_TEXT } from './chromeText';
import { useSpaLink } from './useSpaLink';

/** One breadcrumb entry after the home icon. */
export interface PageCrumb {
  /** Stable key (React key / test hook). */
  key: string;
  label: string;
  /** Absolute path; omitted for the current page. */
  to?: string;
}

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  /** Entries after the home crumb; the last one is the current page. */
  breadcrumbs?: readonly PageCrumb[];
  /** Absolute path of the « Retour » button; omitted ⇒ no button. */
  backTo?: string;
  /** Right-hand slot (status badge, actions). */
  actions?: ReactNode;
}

export function PageHeader({
  title,
  subtitle,
  breadcrumbs,
  backTo,
  actions,
}: Readonly<PageHeaderProps>) {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const spaLink = useSpaLink();
  const homePath = extranetPath(tenantSlug);
  const hasCrumbs = breadcrumbs !== undefined && breadcrumbs.length > 0;

  return (
    <div className="border-b border-secondary-200 bg-white" data-testid="extranet-page-header">
      <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
        <VStack gap={3}>
          {(backTo !== undefined || hasCrumbs) && (
            <HStack gap={3} align="center" wrap>
              {backTo !== undefined && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Icon icon={ChevronLeft} size="xs" />}
                  onClick={spaLink(backTo)}
                >
                  {PAGE_HEADER_TEXT.back}
                </Button>
              )}
              {hasCrumbs && (
                <Breadcrumb
                  aria-label={PAGE_HEADER_TEXT.breadcrumbLabel}
                  separator={<Icon icon={ChevronRight} size="xs" />}
                  maxItems={5}
                >
                  <Breadcrumb.Item href={homePath} onClick={spaLink(homePath)}>
                    <Icon icon={Home} size="xs" aria-label={PAGE_HEADER_TEXT.home} />
                  </Breadcrumb.Item>
                  {breadcrumbs.map((crumb, index) => {
                    const isCurrent = index === breadcrumbs.length - 1;
                    return (
                      <Breadcrumb.Item
                        key={crumb.key}
                        href={isCurrent ? undefined : crumb.to}
                        isCurrent={isCurrent}
                        onClick={crumb.to && !isCurrent ? spaLink(crumb.to) : undefined}
                      >
                        {crumb.label}
                      </Breadcrumb.Item>
                    );
                  })}
                </Breadcrumb>
              )}
            </HStack>
          )}

          <HStack gap={4} align="start" justify="between" wrap>
            <VStack gap={1} className="min-w-0 flex-1">
              <Text as="h1" size="2xl" weight="semibold" color="default">
                {title}
              </Text>
              {subtitle && (
                <Text size="sm" color="muted">
                  {subtitle}
                </Text>
              )}
            </VStack>
            {actions && (
              <HStack gap={2} align="center" wrap>
                {actions}
              </HStack>
            )}
          </HStack>
        </VStack>
      </div>
    </div>
  );
}
